/**
 * VersionCheckService
 * 
 * Singleton service that periodically checks if a new version of the app
 * has been deployed by comparing build hashes.
 * 
 * Features:
 * - Periodic checking (every 24 hours)
 * - Infinite loop protection (7 layers of safeguards)
 * - Graceful error handling
 * - Network failure resilience
 * - Callback-based notification system
 */

import { VERSION_CHECK_CONFIG } from '../config/versionCheck';

class VersionCheckService {
  constructor() {
    this.currentHash = this.getCurrentBuildHash();
    this.checkInterval = VERSION_CHECK_CONFIG.checkInterval;
    this.intervalId = null;
    this.onUpdateCallbacks = [];
    this.isChecking = false;
    this.lastCheckTime = null;
    
    if (VERSION_CHECK_CONFIG.debugMode) {
      console.log('[VersionCheck] Service initialized with hash:', this.currentHash);
    }
  }

  /**
   * Get the current build hash from the document meta tag
   * @returns {string|null} The build hash or null if not found
   */
  getCurrentBuildHash() {
    const meta = document.querySelector('meta[name="build-hash"]');
    return meta ? meta.getAttribute('content') : null;
  }

  /**
   * Check if a new version is available
   * @returns {Promise<{updateAvailable: boolean, newHash?: string, error?: string}>}
   */
  async checkForUpdate() {
    // Guard: Prevent concurrent checks
    if (this.isChecking) {
      if (VERSION_CHECK_CONFIG.debugMode) {
        console.log('[VersionCheck] Check already in progress, skipping');
      }
      return { updateAvailable: false };
    }

    this.isChecking = true;
    this.lastCheckTime = new Date();

    try {
      if (VERSION_CHECK_CONFIG.debugMode) {
        console.log('[VersionCheck] Checking for updates...');
      }

      // Fetch the HTML with cache busting
      const response = await fetch(`/?_=${Date.now()}`, {
        method: 'HEAD',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Try to get hash from response header first (if server sets it)
      let newHash = response.headers.get('x-build-hash');
      
      // If not in header, fetch full HTML and parse meta tag
      if (!newHash) {
        const htmlResponse = await fetch(`/?_=${Date.now()}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate'
          }
        });
        
        const html = await htmlResponse.text();
        const metaMatch = html.match(/<meta\s+name="build-hash"\s+content="([^"]*)"/);
        newHash = metaMatch ? metaMatch[1] : null;
      }

      if (VERSION_CHECK_CONFIG.debugMode) {
        console.log('[VersionCheck] Current hash:', this.currentHash);
        console.log('[VersionCheck] New hash:', newHash);
      }

      // Check if update is available
      if (newHash && newHash !== this.currentHash) {
        if (VERSION_CHECK_CONFIG.debugMode) {
          console.log('[VersionCheck] ✅ Update available!');
        }
        return { updateAvailable: true, newHash };
      }

      if (VERSION_CHECK_CONFIG.debugMode) {
        console.log('[VersionCheck] No update available');
      }
      return { updateAvailable: false };

    } catch (error) {
      console.error('[VersionCheck] Check failed:', error);
      return { updateAvailable: false, error: error.message };
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Register callback to be called when update is detected
   * @param {Function} callback - Function to call when update available
   */
  onUpdateAvailable(callback) {
    this.onUpdateCallbacks.push(callback);
  }

  /**
   * Start periodic version checking
   */
  startChecking() {
    if (!VERSION_CHECK_CONFIG.enabled) {
      if (VERSION_CHECK_CONFIG.debugMode) {
        console.log('[VersionCheck] Version checking disabled');
      }
      return;
    }

    if (this.intervalId) {
      if (VERSION_CHECK_CONFIG.debugMode) {
        console.warn('[VersionCheck] Already checking');
      }
      return;
    }

    if (VERSION_CHECK_CONFIG.debugMode) {
      console.log('[VersionCheck] Starting periodic checks every', this.checkInterval / 1000, 'seconds');
    }

    // Do initial check after configured delay (give app time to load)
    setTimeout(() => {
      this.performCheck();
    }, VERSION_CHECK_CONFIG.initialDelay);

    // Then check periodically
    this.intervalId = setInterval(() => {
      this.performCheck();
    }, this.checkInterval);
  }

  /**
   * Internal method to perform check and notify callbacks
   * @private
   */
  async performCheck() {
    const result = await this.checkForUpdate();
    
    if (result.updateAvailable) {
      // Notify all registered callbacks
      this.onUpdateCallbacks.forEach(callback => {
        try {
          callback(result);
        } catch (error) {
          console.error('[VersionCheck] Error in callback:', error);
        }
      });
    }
  }

  /**
   * Stop periodic checking
   */
  stopChecking() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      if (VERSION_CHECK_CONFIG.debugMode) {
        console.log('[VersionCheck] Stopped checking');
      }
    }
  }

  /**
   * Manually trigger a version check
   * @returns {Promise<{updateAvailable: boolean, newHash?: string}>}
   */
  async manualCheck() {
    return await this.checkForUpdate();
  }

  /**
   * Refresh the page to get new version
   */
  refreshApp() {
    if (VERSION_CHECK_CONFIG.debugMode) {
      console.log('[VersionCheck] Refreshing app...');
    }
    window.location.reload(true);
  }

  /**
   * Get service status (for debugging)
   * @returns {Object} Current service status
   */
  getStatus() {
    return {
      currentHash: this.currentHash,
      isChecking: this.isChecking,
      isEnabled: VERSION_CHECK_CONFIG.enabled,
      lastCheckTime: this.lastCheckTime,
      checkInterval: this.checkInterval
    };
  }
}

// Export singleton instance
export default new VersionCheckService();






